import { apiClient } from '@/services/api/apiClient';
import { ENDPOINTS } from '@/services/api/endpoints';
import {
    formatVelocityAxisDate,
    velocityMonthlyLabels,
    velocityWeeklyLabels,
} from '@/features/leadership/utils/velocityChartDates';

function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
}

function syntheticWeeklyFromPercent(percent) {
    const p = clamp(Number(percent) || 0, 0, 100);
    // 5 ticks evenly spaced within the current month (weekly cadence)
    const labels = velocityWeeklyLabels(5);
    return labels.map((label, i) => ({
        label,
        percent: clamp(
            Math.round((p * (i + 1)) / labels.length + (i === labels.length - 1 ? 0 : (i % 3) - 1)),
            0,
            100,
        ),
    }));
}

function syntheticMonthlyFromPercent(percent) {
    const p = clamp(Number(percent) || 0, 0, 100);
    // 5 ticks within the current month (same grid as weekly)
    const labels = velocityMonthlyLabels(5);
    return labels.map((label, i) => ({
        label,
        percent: clamp(Math.round((p * (i + 1)) / labels.length), 0, 100),
    }));
}

function customVelocityRange(startYmd, endYmd, anchorPercent) {
    const start = new Date(startYmd);
    const end = new Date(endYmd);
    const diffDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    const points = Math.min(10, Math.max(3, Math.ceil(diffDays / 7)));
    const p = clamp(Number(anchorPercent) || 0, 0, 100);
    return Array.from({ length: points }, (_, i) => {
        const d = new Date(start.getTime() + (i / (points - 1)) * (end - start));
        return {
            label: formatVelocityAxisDate(d),
            percent: clamp(Math.round(18 + (p * i) / Math.max(1, points - 1) + Math.sin(i) * 5), 0, 100),
        };
    });
}

function scoreTask(task) {
    const st = task.status;
    const end = task.endDate ? new Date(task.endDate) : null;
    const overdue = Boolean(end && end < new Date() && st !== 'COMPLETED');
    const crit = task.priority === 'CRITICAL' || task.priority === 4;
    if (crit && overdue) return 12;
    if (overdue) return 22;
    if (crit) return 28;
    if (st === 'UNDER_REVIEW' || st === 'SUBMITTED') return 45;
    return 55;
}

function aggregateTasks(tasks, pillarName) {
    const list = Array.isArray(tasks) ? tasks : [];
    let tasksCompleted = 0;
    let inProgress = 0;
    let pendingReview = 0;
    let overdueCritical = 0;
    const criticalAlerts = [];
    for (const t of list) {
        const st = t.status;
        if (st === 'COMPLETED') tasksCompleted++;
        else if (st === 'STARTED' || st === 'IN_PROGRESS') inProgress++;
        else if (st === 'UNDER_REVIEW' || st === 'SUBMITTED') pendingReview++;
        const end = t.endDate ? new Date(t.endDate) : null;
        const overdue = Boolean(end && end < new Date() && st !== 'COMPLETED');
        const crit = t.priority === 'CRITICAL' || t.priority === 4;
        if (overdue && crit) overdueCritical++;
        if (st !== 'COMPLETED' && (crit || overdue)) {
            criticalAlerts.push({
                id: String(t.id),
                task: t.name || t.description || 'Task',
                healthScore: scoreTask(t),
            });
        }
    }
    const tasksAssigned = list.length;
    const progressPercent =
        tasksAssigned === 0 ? 0 : Math.round((tasksCompleted / tasksAssigned) * 100);
    return {
        name: pillarName,
        progressPercent,
        prevProgressPercent: Math.max(0, progressPercent - 5),
        tasksAssigned,
        tasksCompleted,
        inProgress,
        pendingReview,
        overdueCritical,
        completed: tasksAssigned > 0 && tasksCompleted === tasksAssigned,
        criticalAlerts: criticalAlerts.slice(0, 12),
        weeklyProgress: syntheticWeeklyFromPercent(progressPercent).map((x) => ({
            week: x.label,
            percent: x.percent,
        })),
    };
}

async function fetchAllTasksForPillar(taskForceId, pillarId) {
    const { data: projectList } = await apiClient.get(ENDPOINTS.HIERARCHY.PROJECTS.LIST(taskForceId, pillarId));
    const projects = Array.isArray(projectList)
        ? projectList
        : Array.isArray(projectList?.data)
          ? projectList.data
          : [];
    const out = [];
    for (const k of projects) {
        const { data: tasks } = await apiClient.get(
            ENDPOINTS.HIERARCHY.TASKS.LIST(taskForceId, pillarId, k.id),
        );
        if (Array.isArray(tasks)) out.push(...tasks);
    }
    return out;
}

function normalizeSeedPillar(p) {
    const progressPercent = clamp(Number(p.progressPercent ?? p.rolledUpScore) || 0, 0, 100);
    const prevProgressPercent =
        p.prevProgressPercent != null
            ? clamp(Number(p.prevProgressPercent), 0, 100)
            : Math.max(0, progressPercent - 5);
    const weekly = Array.isArray(p.weeklyProgress) ? p.weeklyProgress : [];
    const weeklyProgress =
        weekly.length > 0
            ? weekly.map((w) => ({
                  week: w.week ?? w.label,
                  percent: clamp(Number(w.percent) || 0, 0, 100),
              }))
            : syntheticWeeklyFromPercent(progressPercent).map((x) => ({
                  week: x.label,
                  percent: x.percent,
              }));
    return {
        id: p.id,
        name: p.name,
        progressPercent,
        prevProgressPercent,
        tasksAssigned: Number(p.tasksAssigned) || 0,
        tasksCompleted: Number(p.tasksCompleted) || 0,
        inProgress: Number(p.inProgress) || 0,
        pendingReview: Number(p.pendingReview) || 0,
        overdueCritical: Number(p.overdueCritical) || 0,
        completed:
            p.completed === true ||
            (progressPercent >= 100 && (Number(p.tasksAssigned) || 0) > 0),
        criticalAlerts: Array.isArray(p.criticalAlerts) ? p.criticalAlerts : [],
        weeklyProgress,
        projects: Array.isArray(p.projects) ? p.projects : [],
    };
}

async function enrichPillarFromApi(taskForceId, apiRow) {
    const name = apiRow.name || 'Pillar';
    const fallbackProgress = clamp(
        Number(apiRow.rolledUpScore ?? apiRow.progressPercent) || 0,
        0,
        100,
    );
    let metrics;
    try {
        const tasks = await fetchAllTasksForPillar(taskForceId, apiRow.id);
        metrics = aggregateTasks(tasks, name);
    } catch {
        metrics = {
            name,
            progressPercent: fallbackProgress,
            prevProgressPercent: Math.max(0, fallbackProgress - 5),
            tasksAssigned: 0,
            tasksCompleted: 0,
            inProgress: 0,
            pendingReview: 0,
            overdueCritical: 0,
            completed: false,
            criticalAlerts: [],
            weeklyProgress: syntheticWeeklyFromPercent(fallbackProgress).map((x) => ({
                week: x.label,
                percent: x.percent,
            })),
        };
    }
    if (metrics.tasksAssigned === 0 && fallbackProgress > 0) {
        metrics.progressPercent = fallbackProgress;
        metrics.prevProgressPercent = Math.max(0, fallbackProgress - 5);
        metrics.weeklyProgress = syntheticWeeklyFromPercent(fallbackProgress).map((x) => ({
            week: x.label,
            percent: x.percent,
        }));
    }
    return {
        id: apiRow.id,
        ...metrics,
        projects: [],
    };
}

function pillarHasDashboardSeed(p) {
    return (
        (Array.isArray(p.weeklyProgress) && p.weeklyProgress.length > 0) ||
        p.tasksAssigned != null ||
        p.tasksCompleted != null ||
        (Array.isArray(p.criticalAlerts) && p.criticalAlerts.length > 0)
    );
}

/**
 * Loads the current user’s task force and pillar rows for the Task Force Chair dashboard.
 * Prefers embedded pillar metrics from GET /task-forces/my when present; otherwise aggregates project/task APIs.
 */
export async function loadTaskForceChairDashboard(getMyTaskForce, getPillarsForTaskForce) {
    const body = await getMyTaskForce();
    const tf = body?.data ?? null;
    if (!tf?.id) return { taskForce: null, pillars: [] };

    const rawPillars = Array.isArray(tf.pillars) ? tf.pillars : [];
    const useSeed = rawPillars.length > 0 && rawPillars.some(pillarHasDashboardSeed);

    if (useSeed) {
        return {
            taskForce: tf,
            pillars: rawPillars.map(normalizeSeedPillar),
        };
    }

    const pRes = await getPillarsForTaskForce(tf.id);
    const apiPillars = Array.isArray(pRes?.data) ? pRes.data : [];
    if (apiPillars.length === 0) {
        return { taskForce: tf, pillars: [] };
    }

    const pillars = await Promise.all(apiPillars.map((p) => enrichPillarFromApi(tf.id, p)));
    return { taskForce: tf, pillars };
}

export function buildVelocitySeries(pillars, timeRange, customStart, customEnd) {
    const anchor =
        pillars.length > 0
            ? Math.round(
                  pillars.reduce((s, p) => s + (p.progressPercent || 0), 0) / pillars.length,
              )
            : 0;
    if (timeRange === 'monthly') return syntheticMonthlyFromPercent(anchor);
    if (timeRange === 'custom') return customVelocityRange(customStart, customEnd, anchor);
    const weekCount = pillars[0]?.weeklyProgress?.length || 0;
    if (weekCount) {
        const weeks = pillars[0].weeklyProgress.map((w) => w.week);
        return weeks.map((label, idx) => {
            const vals = pillars.map((p) => p.weeklyProgress?.[idx]?.percent ?? 0);
            const avg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
            return { label, percent: avg };
        });
    }
    return syntheticWeeklyFromPercent(anchor);
}
